import * as React from 'react';

import { CartesianGrid, ComposedChart, XAxis, YAxis } from 'recharts';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig
} from 'src/component/ds/ui/chart';
import { type ChartScale, axisProps, scaleSeries } from 'src/component/ds/ui/chartAxis';


export interface ComboChartSeries {
  key: string;
  scale?: (max: number) => ChartScale | null;
}

export interface ComboChartAxis {
  id: string;
  series: ComboChartSeries[];
  position?: 'left' | 'right';
  unit?: string;
  baselineAtZero?: boolean;
  width?: number;
  hide?: boolean;
}

export interface ComboChartRenderProps {
  data: Record<string, any>[];
  units: Record<string, string | undefined>;
}

type ComboChartProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  config: ChartConfig,
  data: Record<string, any>[],
  axes: ComboChartAxis[],
  /** Row key for the category axis. */
  xKey: string,
  children: (props: ComboChartRenderProps) => React.ReactNode,
  /** Replaces the default tooltip; `false` omits it. */
  tooltip?: React.ReactElement | false,
  margin?: React.ComponentProps<typeof ComposedChart>['margin'],
};

const DEFAULT_MARGIN = { top: 4, right: 4, bottom: 0, left: 4 };

/**
 * A bar/line chart with N value axes
 *
 * Height comes from className -- ChartContainer has no intrinsic size.
 *
 * @param {ComboChartProps} props see ComboChartProps
 * @returns {JSX.Element} the chart
 */
export function ComboChart ({
  config,
  data,
  axes,
  xKey,
  children,
  tooltip,
  margin = DEFAULT_MARGIN,
  ...props
}: ComboChartProps) {
  /* One pass over every axis' series, since scaleSeries rewrites whole rows. */
  const scaleByColumn = React.useMemo(() => {
    const byColumn: Record<string, (max: number) => ChartScale | null> = {};

    for (const axis of axes) {
      for (const series of axis.series) {
        if (series.scale) byColumn[series.key] = series.scale;
      }
    }

    return byColumn;
  }, [axes]);

  const { data: scaledData, units } = React.useMemo(
    () => scaleSeries(data, scaleByColumn),
    [data, scaleByColumn]
  );

  /* Ticks come from every series on the axis, not just the drawn ones, so toggling a
   * series off in the legend doesn't rescale what's left. */
  const resolvedAxes = React.useMemo(() => axes.map((axis) => {
    const columns = axis.series.map((series) => series.key);
    const scaledUnit = columns.map((column) => units[column]).find(Boolean);

    return {
      axis,
      props: axisProps(scaledData, columns, axis.unit ?? scaledUnit, {
        baselineAtZero: axis.baselineAtZero,
      }),
    };
  }), [axes, scaledData, units]);

  return (
    <ChartContainer config={config} {...props}>
      <ComposedChart data={scaledData} margin={margin}>
        <CartesianGrid vertical={false} />

        <XAxis dataKey={xKey} tickLine={false} axisLine={false} interval='preserveStartEnd' />

        {resolvedAxes.map(({ axis, props: axisTickProps }) => (
          <YAxis
            key={axis.id}
            yAxisId={axis.id}
            orientation={axis.position ?? 'left'}
            hide={axis.hide}
            width={axis.width}
            tickLine={false}
            axisLine={false}
            {...axisTickProps}
          />
        ))}

        {tooltip === false ? null : tooltip ?? <ChartTooltip content={<ChartTooltipContent />} />}

        {children({ data: scaledData, units })}
      </ComposedChart>
    </ChartContainer>
  );
}
